import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  ClassicEditor,
  Essentials,
  Autoformat,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Subscript,
  Superscript,
  BlockQuote,
  Heading,
  Link,
  List,
  TodoList,
  Indent,
  IndentBlock,
  Alignment,
  Font,
  HorizontalLine,
  RemoveFormat,
  Paragraph,
  PasteFromOffice,
  Table,
  TableToolbar,
  TableProperties,
  TableCellProperties,
  TableColumnResize,
  TableCaption,
  Image,
  ImageCaption,
  ImageStyle,
  ImageToolbar,
  ImageResize,
  ImageUpload,
  ImageInsert,
  Base64UploadAdapter,
  GeneralHtmlSupport,
  HtmlEmbed,
  SourceEditing,
  FindAndReplace,
  SpecialCharacters,
  SpecialCharactersEssentials,
  Plugin,
  ButtonView,
  UpcastWriter,
  Widget,
  toWidget,
} from "ckeditor5";

import katex from "katex";

import "katex/dist/katex.min.css";
import "ckeditor5/ckeditor5.css";
import "./mathEditor.css";

// Quill used to read this off `window`; we keep it here so other rendered KaTeX
// output around the app continues to find it.
window.katex = katex;

// ---------------------------------------------------------------------------
// Math button
// ---------------------------------------------------------------------------

const MATH_ICON =
  '<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">' +
  '<text x="2" y="15" font-size="13" font-family="serif" font-style="italic">f(x)</text>' +
  "</svg>";

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function renderLatexHtml(latex) {
  try {
    return katex.renderToString(latex, {
      throwOnError: false,
      output: "html",
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("KaTeX render error:", err);
    return escapeHtml(latex);
  }
}

/**
 * Inline math widget.
 *
 * CKEditor's model is structural, not visual — if we tried to insert KaTeX's
 * deeply-nested span layout (positioned superscripts, struts, etc.) as plain
 * inline HTML, the upcast/downcast pipeline would flatten it and you'd lose
 * the superscript/fraction styling. Instead we register a custom model
 * element `mathFormula` that stores the original LaTeX in a `latex`
 * attribute. The data view and editing view both render the KaTeX HTML
 * fresh from that attribute every time, so:
 *
 *   - The visual rendering always matches what KaTeX would produce.
 *   - The saved HTML is `<span class="math-formula" data-latex="…">…</span>`
 *     and round-trips through save/load without degradation.
 *   - In the editor, the math behaves as a single atomic "widget" (selecting
 *     it selects the whole formula, deleting deletes the whole thing).
 */
class MathFormulaPlugin extends Plugin {
  static get pluginName() {
    return "MathFormulaPlugin";
  }

  static get requires() {
    return [Widget];
  }

  init() {
    this._defineSchema();
    this._defineConverters();
    this._defineUI();
  }

  _defineSchema() {
    this.editor.model.schema.register("mathFormula", {
      allowWhere: "$text",
      isInline: true,
      isObject: true,
      allowAttributes: ["latex"],
    });
  }

  _defineConverters() {
    const conversion = this.editor.conversion;

    // HTML → model: <span class="math-formula" data-latex="…">…</span>
    conversion.for("upcast").elementToElement({
      view: {
        name: "span",
        classes: "math-formula",
      },
      model: (viewElement, { writer }) => {
        const latex =
          viewElement.getAttribute("data-latex") ||
          // Fallback: try the rendered `.katex` annotation if `data-latex`
          // is missing (older saves).
          "";
        return writer.createElement("mathFormula", { latex });
      },
      // Higher than text-content conversion so nested KaTeX spans don't
      // get individually upcast.
      converterPriority: "high",
    });

    // model → editing view (what the user sees while editing).
    // `toWidget()` requires a ContainerElement, so we make the outer wrapper
    // a container and put the rendered KaTeX HTML in a RawElement child.
    conversion.for("editingDowncast").elementToElement({
      model: "mathFormula",
      view: (modelElement, { writer }) => {
        const container = createMathEditingContainer(modelElement, writer);
        return toWidget(container, writer, {
          label: "math formula",
        });
      },
    });

    // model → data view (what `editor.getData()` returns and what's saved).
    // A single RawElement is enough here — it serializes as
    // `<span class="math-formula" data-latex="…">…rendered KaTeX…</span>`.
    conversion.for("dataDowncast").elementToElement({
      model: "mathFormula",
      view: (modelElement, { writer }) =>
        createMathRawElement(modelElement, writer),
    });
  }

  _defineUI() {
    const editor = this.editor;
    editor.ui.componentFactory.add("mathFormula", (locale) => {
      const view = new ButtonView(locale);
      view.set({
        label: "Insert math formula (LaTeX)",
        tooltip: true,
        withText: false,
        icon: MATH_ICON,
      });

      view.on("execute", () => {
        // If the current selection is on an existing math element, prefill
        // its current LaTeX so the prompt behaves as "edit formula".
        const selected = editor.model.document.selection.getSelectedElement();
        const existing =
          selected && selected.is("element", "mathFormula")
            ? selected.getAttribute("latex") || ""
            : "";

        // eslint-disable-next-line no-alert
        const latex = window.prompt(
          "Enter LaTeX (e.g. \\frac{a}{b}, x^2, \\sqrt{x}):",
          existing
        );
        if (latex === null) return;
        const trimmed = latex.trim();
        if (!trimmed) return;

        editor.model.change((writer) => {
          if (selected && selected.is("element", "mathFormula")) {
            writer.setAttribute("latex", trimmed, selected);
            return;
          }
          const node = writer.createElement("mathFormula", { latex: trimmed });
          editor.model.insertContent(node);
          writer.setSelection(node, "on");
        });
      });

      return view;
    });
  }
}

function createMathRawElement(modelElement, writer) {
  const latex = modelElement.getAttribute("latex") || "";
  const rendered = renderLatexHtml(latex);
  return writer.createRawElement(
    "span",
    {
      class: "math-formula",
      "data-latex": latex,
    },
    (domElement) => {
      domElement.innerHTML = rendered;
    }
  );
}

function createMathEditingContainer(modelElement, writer) {
  const latex = modelElement.getAttribute("latex") || "";
  const rendered = renderLatexHtml(latex);

  // The outer span must be a ContainerElement so `toWidget()` accepts it.
  const container = writer.createContainerElement("span", {
    class: "math-formula",
    "data-latex": latex,
  });

  // The rendered KaTeX HTML lives inside a RawElement child — RawElement is
  // CKEditor's way to inject opaque, untouchable DOM.
  const inner = writer.createRawElement(
    "span",
    { class: "math-formula-render" },
    (domElement) => {
      domElement.innerHTML = rendered;
    }
  );

  writer.insert(writer.createPositionAt(container, 0), inner);
  return container;
}

// ---------------------------------------------------------------------------
// Paste cleanup
//
// Word puts a LOT of junk on the clipboard. The PasteFromOffice plugin handles
// most of it, but `<p>` elements containing only `&nbsp;` are technically
// valid content (a paragraph with whitespace), so editors keep them. They
// show up in your editor as empty space and as `<p>&nbsp;&nbsp;…</p>` in
// source view.
//
// We intercept the clipboard HTML *before* CKEditor parses it, walk the DOM,
// and:
//   1. Drop `<o:p>` Word namespace placeholders.
//   2. Drop `<!--[if ...]>...<![endif]-->` MS Office conditional comments.
//   3. Iteratively remove empty `<p>` / `<div>` / `<h1..6>` / `<span>` whose
//      text content is whitespace-only (after stripping nbsp/zwsp) AND whose
//      children contain nothing meaningful (`<img>`, `<table>`, `<br>`, ...).
//   4. Collapse runs of 3+ whitespace/nbsp chars in text nodes down to a
//      single space (Word's "indent with 30 nbsps" trick).
// ---------------------------------------------------------------------------

const REMOVABLE_BLOCK_TAG = /^(P|DIV|H[1-6])$/;
const SIGNIFICANT_CHILDREN_SELECTOR =
  "img,table,hr,figure,video,iframe,svg,br,input,canvas,audio,picture,source";
const WHITESPACE_ONLY_RE = /^[\s\u00A0\u200B]*$/;
const LONG_WHITESPACE_RUN_RE = /[\s\u00A0\u200B]{3,}/g;

function isWhitespaceOnly(text) {
  return WHITESPACE_ONLY_RE.test(text || "");
}

// Office (Word / WPS Kingsoft / etc.) hides real content in two patterns that
// a plain HTML parser treats as comments / unknown elements:
//
//   1. Conditional comments
//      `<!--[if !vml]>      …actual content…  <![endif]-->`   downlevel-hidden
//      `<![if !supportLists]>…actual content…<![endif]>`      downlevel-revealed
//   2. Office namespace tags
//      `<o:p>`, `<v:shape>`, `<w:wordDocument>`, …
//
// If we don't unwrap these before parsing, the text inside becomes "comment
// text" (which doesn't contribute to `Element.textContent`) and the outer
// `<p>` looks empty to the empty-block sweeper — so we'd delete real content.
function preExposeOfficeContent(html) {
  let out = html;

  // Downlevel-hidden:  <!--[if X]> … <![endif]-->
  //  - `[if !X]` is the "show to NON-X browsers" branch — that's us. Keep its content.
  //  - `[if X]`  is MSO/VML/IE-only markup — strip entirely (we can't render it).
  out = out.replace(
    /<!--\s*\[if\s+([^\]]+)\]\s*>([\s\S]*?)<!\s*\[endif\]\s*-->/gi,
    (_m, cond, content) => (cond.trim().startsWith("!") ? content : "")
  );

  // Downlevel-revealed: <![if X]> … <![endif]>  — content is always visible
  // to browsers; just strip the markers.
  out = out
    .replace(/<!\s*\[if[^\]]*\]\s*>/gi, "")
    .replace(/<!\s*\[endif\]\s*>/gi, "");

  // Office / VML / Word namespace tags — strip the tag, KEEP the text inside.
  // Matches `<o:p>`, `</o:p>`, `<v:shape …/>`, `<w:something>`, etc.
  out = out.replace(
    /<\/?[a-z]+:[a-z][a-z0-9]*(?:\s[^>]*)?\/?>/gi,
    ""
  );

  return out;
}

function cleanPastedHtml(html) {
  if (!html || typeof window === "undefined" || !window.DOMParser) return html;

  const pre = preExposeOfficeContent(html);

  const doc = new window.DOMParser().parseFromString(pre, "text/html");
  if (!doc || !doc.body) return html;

  // Drop images we know can't render in a browser. Word / WPS embed images
  // as `file:///…/ksohtml…/wps8.jpg`, `cid:…`, `mhtml:…`, etc., which are
  // OS-local URIs the browser is not allowed to fetch. They show up as
  // broken-image icons. Better to drop them; the user can drag/drop or
  // paste the image on its own and `Base64UploadAdapter` will inline it.
  doc.body.querySelectorAll("img[src]").forEach((img) => {
    const src = (img.getAttribute("src") || "").trim();
    if (/^(file:|cid:|mhtml:|x-msole:)/i.test(src)) {
      img.remove();
    }
  });

  // Iterative pass: removing one empty block can make its parent empty.
  // We deliberately skip anything inside a <table> so we never strip table
  // cells or content Word puts inside them.
  for (let pass = 0; pass < 5; pass += 1) {
    let removed = false;

    const blocks = doc.body.querySelectorAll("p,div,h1,h2,h3,h4,h5,h6");
    blocks.forEach((el) => {
      if (!REMOVABLE_BLOCK_TAG.test(el.tagName)) return;
      if (el.closest("table")) return;
      if (el.querySelector(SIGNIFICANT_CHILDREN_SELECTOR)) return;
      if (isWhitespaceOnly(el.textContent)) {
        el.remove();
        removed = true;
      }
    });

    const spans = doc.body.querySelectorAll("span");
    spans.forEach((el) => {
      if (el.closest("table")) return;
      if (el.querySelector(SIGNIFICANT_CHILDREN_SELECTOR)) return;
      if (el.children.length > 0) return;
      if (isWhitespaceOnly(el.textContent)) {
        el.remove();
        removed = true;
      }
    });

    if (!removed) break;
  }

  // Collapse long whitespace runs inside surviving text nodes (but not
  // inside table cells, where users may rely on spacing).
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, null);
  const textNodes = [];
  let n;
  while ((n = walker.nextNode())) textNodes.push(n);
  for (const tn of textNodes) {
    if (tn.parentElement && tn.parentElement.closest("table")) continue;
    const before = tn.textContent;
    if (!before) continue;
    const after = before.replace(LONG_WHITESPACE_RUN_RE, " ");
    if (after !== before) tn.textContent = after;
  }

  return doc.body.innerHTML;
}

// --- View-fragment cleanup (Stage 2, runs after PasteFromOffice) -----------

function viewElementHasSignificantDescendant(viewElement) {
  if (!viewElement || !viewElement.getChildren) return false;
  for (const child of viewElement.getChildren()) {
    if (!child.is || !child.is("element")) continue;
    const name = (child.name || "").toLowerCase();
    if (/^(img|table|hr|figure|video|iframe|svg|br|input|canvas|audio|picture|source)$/.test(name)) {
      return true;
    }
    if (viewElementHasSignificantDescendant(child)) return true;
  }
  return false;
}

function viewElementTextContent(viewElement) {
  let text = "";
  const walk = (node) => {
    if (!node || !node.getChildren) return;
    for (const child of node.getChildren()) {
      if (child.is && child.is("$text")) text += child.data || "";
      else if (child.is && child.is("element")) walk(child);
    }
  };
  walk(viewElement);
  return text;
}

function viewHasTableAncestor(viewElement) {
  let n = viewElement.parent;
  while (n) {
    if (n.is && n.is("element") && /^(table|td|th|tr|tbody|thead|tfoot)$/i.test(n.name)) {
      return true;
    }
    n = n.parent;
  }
  return false;
}

function collectEmptyBlocksInView(root) {
  const empties = [];
  const walk = (node) => {
    if (!node || !node.getChildren) return;
    for (const child of node.getChildren()) {
      walk(child);
      if (!child.is || !child.is("element")) continue;
      const name = (child.name || "").toLowerCase();
      if (!/^(p|div|h[1-6]|span)$/.test(name)) continue;
      if (viewHasTableAncestor(child)) continue;
      if (viewElementHasSignificantDescendant(child)) continue;
      if (isWhitespaceOnly(viewElementTextContent(child))) {
        empties.push(child);
      }
    }
  };
  walk(root);
  return empties;
}

class PasteCleanupPlugin extends Plugin {
  static get pluginName() {
    return "PasteCleanupPlugin";
  }

  init() {
    const editor = this.editor;
    const viewDocument = editor.editing.view.document;

    // ------------------------------------------------------------------
    // Stage 1: HTML-string cleanup at `clipboardInput` (priority: high).
    // Runs before the default handler parses the clipboard HTML.
    // ------------------------------------------------------------------
    viewDocument.on(
      "clipboardInput",
      (_evt, data) => {
        if (!data || !data.dataTransfer) return;
        const html = data.dataTransfer.getData("text/html");
        if (!html) return;
        const cleaned = cleanPastedHtml(html);
        if (!cleaned || cleaned === html) return;
        try {
          data.content = editor.data.htmlProcessor.toView(cleaned);
          // eslint-disable-next-line no-console
          console.debug("[MathEditor] paste cleanup (stage 1) ran");
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("[MathEditor] paste cleanup (stage 1) failed:", err);
        }
      },
      { priority: "high" }
    );

    // ------------------------------------------------------------------
    // Stage 1b: when the clipboard ALSO contains real image file blobs
    // (Word / WPS put them on the clipboard alongside the `file://` HTML
    // references), schedule them to be inserted into the editor as
    // base64 right after the paste settles. Without this we'd drop the
    // image entirely in the bulk-paste case.
    // ------------------------------------------------------------------
    viewDocument.on(
      "clipboardInput",
      (_evt, data) => {
        if (!data || !data.dataTransfer) return;
        const dt = data.dataTransfer;
        // We only care about files when there's ALSO HTML — pure image
        // paste is already handled correctly by ImageUpload itself.
        const html = dt.getData("text/html") || "";
        if (!html) return;
        const files = [];
        // `dataTransfer.files` is the canonical FileList for clipboard files.
        // CKEditor's DataTransfer wrapper exposes it through `.files`.
        const fileList = dt.files || [];
        for (let i = 0; i < fileList.length; i += 1) {
          const f = fileList[i];
          if (f && f.type && f.type.startsWith("image/")) files.push(f);
        }
        if (files.length === 0) return;

        // Read each file to base64 and append <img> nodes to the document
        // after the current paste tick.
        Promise.all(
          files.map(
            (file) =>
              new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = () => resolve(null);
                reader.readAsDataURL(file);
              })
          )
        ).then((base64s) => {
          const valid = base64s.filter(Boolean);
          if (valid.length === 0) return;
          editor.model.change((writer) => {
            const root = editor.model.document.getRoot();
            const insertAt = writer.createPositionAt(root, "end");
            for (const dataUrl of valid) {
              const imageElement = writer.createElement("imageBlock", {
                src: dataUrl,
              });
              writer.insert(imageElement, insertAt);
            }
          });
        });
      },
      { priority: "high" }
    );

    // ------------------------------------------------------------------
    // Stage 2: view-fragment cleanup at `inputTransformation` (priority: low).
    // Runs AFTER PasteFromOffice has normalized Word's HTML. We walk the
    // parsed view fragment and strip any remaining empty-block leftovers
    // that survived stage 1 (or were re-introduced by normalization).
    // ------------------------------------------------------------------
    editor.plugins.get("ClipboardPipeline").on(
      "inputTransformation",
      (_evt, data) => {
        if (!data || !data.content) return;
        const empties = collectEmptyBlocksInView(data.content);
        if (empties.length === 0) return;
        const writer = new UpcastWriter(editor.editing.view.document);
        for (const el of empties) {
          // Some nodes may already be detached if a parent was removed earlier
          // in this pass; guard against that.
          if (el.parent) writer.remove(el);
        }
        // eslint-disable-next-line no-console
        console.debug(
          `[MathEditor] paste cleanup (stage 2) removed ${empties.length} empty block(s)`
        );
      },
      { priority: "low" }
    );
  }
}

// ---------------------------------------------------------------------------
// Editor config
// ---------------------------------------------------------------------------

const TOOLBAR_ITEMS = [
  "undo",
  "redo",
  "|",
  "heading",
  "|",
  "bold",
  "italic",
  "underline",
  "strikethrough",
  "subscript",
  "superscript",
  "removeFormat",
  "|",
  "fontSize",
  "fontFamily",
  "fontColor",
  "fontBackgroundColor",
  "|",
  "alignment",
  "bulletedList",
  "numberedList",
  "todoList",
  "outdent",
  "indent",
  "|",
  "link",
  "insertImage",
  "insertTable",
  "blockQuote",
  "horizontalLine",
  "specialCharacters",
  "mathFormula",
  "|",
  "findAndReplace",
  "sourceEditing",
];

const COMPACT_TOOLBAR_ITEMS = [
  "undo",
  "redo",
  "|",
  "bold",
  "italic",
  "underline",
  "|",
  "bulletedList",
  "numberedList",
  "|",
  "link",
  "insertImage",
  "mathFormula",
];

const PLUGINS = [
  Essentials,
  Autoformat,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Subscript,
  Superscript,
  BlockQuote,
  Heading,
  Link,
  List,
  TodoList,
  Indent,
  IndentBlock,
  Alignment,
  Font,
  HorizontalLine,
  RemoveFormat,
  Paragraph,
  PasteFromOffice,
  Table,
  TableToolbar,
  TableProperties,
  TableCellProperties,
  TableColumnResize,
  TableCaption,
  Image,
  ImageCaption,
  ImageStyle,
  ImageToolbar,
  ImageResize,
  ImageUpload,
  ImageInsert,
  Base64UploadAdapter,
  GeneralHtmlSupport,
  HtmlEmbed,
  SourceEditing,
  FindAndReplace,
  SpecialCharacters,
  SpecialCharactersEssentials,
  MathFormulaPlugin,
  PasteCleanupPlugin,
];

const BASE_CONFIG = {
  licenseKey: "GPL",
  plugins: PLUGINS,
  toolbar: { items: TOOLBAR_ITEMS, shouldNotGroupWhenFull: true },
  heading: {
    options: [
      { model: "paragraph", title: "Paragraph", class: "ck-heading_paragraph" },
      { model: "heading1", view: "h1", title: "Heading 1", class: "ck-heading_heading1" },
      { model: "heading2", view: "h2", title: "Heading 2", class: "ck-heading_heading2" },
      { model: "heading3", view: "h3", title: "Heading 3", class: "ck-heading_heading3" },
    ],
  },
  image: {
    toolbar: [
      "imageTextAlternative",
      "toggleImageCaption",
      "imageStyle:inline",
      "imageStyle:block",
      "imageStyle:side",
      "|",
      "resizeImage",
    ],
    insert: { type: "auto" },
  },
  table: {
    contentToolbar: [
      "tableColumn",
      "tableRow",
      "mergeTableCells",
      "tableProperties",
      "tableCellProperties",
      "toggleTableCaption",
    ],
  },
  // Permissive HTML support so:
  //   1. KaTeX-rendered `<span class="math-formula">` nodes are preserved.
  //   2. Word/Excel paste keeps its inline styles, table classes, etc.
  htmlSupport: {
    allow: [
      {
        name: /.*/,
        attributes: true,
        classes: true,
        styles: true,
      },
    ],
  },
};

/**
 * Rich-text editor backed by CKEditor 5.
 *
 * Features:
 *   - Type text with formatting, headings, lists, tables, links, etc.
 *   - Drag-and-drop or paste images (auto-inlined as base64 via `Base64UploadAdapter`).
 *   - Insert math formulas via the toolbar button — LaTeX is rendered with KaTeX
 *     and stored as `<span class="math-formula" data-latex="…">…</span>`.
 *   - Paste mixed Word content (text + tables + images). `PasteFromOffice` handles
 *     the Word HTML; `PasteCleanupPlugin` strips empty `&nbsp;`-padded paragraphs
 *     Word inserts everywhere.
 *
 * @param {string} [value]                       Controlled HTML value.
 * @param {(html: string) => void} [onChange]    Fires with the latest HTML on every change.
 * @param {boolean} [readOnly]                   Disables editing.
 * @param {boolean} [compactToolbar]             Shorter toolbar. Extra buttons collapse instead of wrapping.
 * @param {string}  [placeholder]                Placeholder shown when empty.
 * @param {object}  [style]                      Wrapper style overrides.
 */
export default function MathEditor({
  value: valueProp,
  onChange,
  readOnly = false,
  compactToolbar = false,
  placeholder = "Write something... (supports math, images, tables, and paste from Word)",
  style,
}) {
  const [internalValue, setInternalValue] = useState("");
  const isControlled = valueProp !== undefined;
  const value = isControlled ? valueProp : internalValue;

  const config = useMemo(
    () => ({
      ...BASE_CONFIG,
      placeholder,
      toolbar: {
        items: compactToolbar ? COMPACT_TOOLBAR_ITEMS : TOOLBAR_ITEMS,
        shouldNotGroupWhenFull: !compactToolbar,
      },
    }),
    [placeholder, compactToolbar]
  );
  const { height, minHeight, ...restStyle } = style || {};
  const fullMinHeight = compactToolbar ? undefined : height ?? minHeight ?? 240;
  const editorStyle = {
    maxWidth: "100%",
    minHeight: 100,
    ...restStyle,
  };
  if (!compactToolbar) {
    editorStyle["--math-editor-min-height"] =
      typeof fullMinHeight === "number" ? `${fullMinHeight}px` : fullMinHeight;
  } else {
    Object.assign(editorStyle, style);
  }

  return (
    <div
      className={`math-editor${compactToolbar ? "" : " math-editor--full"}`}
      style={editorStyle}
    >
      <CKEditor
        editor={ClassicEditor}
        data={value || ""}
        disabled={readOnly}
        config={config}
        onChange={(_event, editor) => {
          const html = editor.getData();
          onChange?.(html);
          if (!isControlled) setInternalValue(html);
        }}
      />
    </div>
  );
}

MathEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
  compactToolbar: PropTypes.bool,
  placeholder: PropTypes.string,
  style: PropTypes.object,
};
